import ROLES from 'common/constants/userRoles';
import { PERMISSIONS } from 'common/constants/userPermissions';

export const DISABLE_REASON = {
  NO_ADD_THERAPISTS: 'NO_ADD_THERAPISTS',
  NO_EDIT_USER_PERMISSIONS: 'NO_EDIT_USER_PERMISSIONS',
  SELF_EDIT_FORBIDDEN: 'SELF_EDIT_FORBIDDEN',
  SUPPLIER_BOUND: 'SUPPLIER_BOUND',
  TARGET_ADMIN_IMMUTABLE: 'TARGET_ADMIN_IMMUTABLE',
  NON_ADMIN_CANNOT_EDIT_ADMIN: 'NON_ADMIN_CANNOT_EDIT_ADMIN',
  SELF_DEACTIVATE: 'SELF_DEACTIVATE',
  PERMISSION_NOT_HELD_BY_ACTOR: 'PERMISSION_NOT_HELD_BY_ACTOR',
  PERMISSION_IMPLIED: 'PERMISSION_IMPLIED',
  CANNOT_GRANT_FULL_PRESET_ROLE: 'CANNOT_GRANT_FULL_PRESET_ROLE',
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
} as const;

export type DisableReason = typeof DISABLE_REASON[keyof typeof DISABLE_REASON];

export interface ActorContext {
  permissions: string[];
  roleCode: string | null;
  employeeId: number | null;
  masqueradingAsSupplier: boolean;
}

export interface TargetEmployee {
  id: number | null;
  role: string | null;
  persistedRole?: string | null;
  permissions: string[];
  supplierBound: boolean;
  active: boolean;
}

export interface RolePermissions {
  role: string;
  permissions: string[];
}

export interface PolicyResult {
  allowed: boolean;
  reason?: DisableReason;
}

export interface ResolveRolePermissionsOptions {
  isCreate?: boolean;
  currentTarget?: {
    role: string | null;
    permissions: string[];
  };
}

const IMPLIED_PERMISSIONS = new Set<string>([PERMISSIONS.viewOwnCalendar]);

const hasPermission = (permissions: string[], tag: string): boolean => permissions.includes(tag);

const isAdministratorRole = (roleCode: string | null | undefined): boolean =>
  roleCode === ROLES.ADMINISTRATOR;

export const canSelectAdministratorRole = (actor: ActorContext): boolean =>
  actor.masqueradingAsSupplier || isAdministratorRole(actor.roleCode);

const isSelfEdit = (actor: ActorContext, target: TargetEmployee): boolean =>
  actor.employeeId != null && target.id != null && actor.employeeId === target.id;

export const getEffectiveTargetRoleForRolePolicy = (target: TargetEmployee): string | null => {
  if (target.id == null) {
    return target.role;
  }

  return target.persistedRole ?? target.role;
};

export const isImpliedPermission = (permissionSlug: string): boolean =>
  IMPLIED_PERMISSIONS.has(permissionSlug);

export const canAddTeamMember = (actorPermissions: string[]): boolean =>
  hasPermission(actorPermissions, PERMISSIONS.addTherapists);

export const canGrantPermission = (actorPermissions: string[], permissionSlug: string): boolean => {
  if (!canAddTeamMember(actorPermissions)) {
    return false;
  }

  if (isImpliedPermission(permissionSlug)) {
    return false;
  }
  return hasPermission(actorPermissions, permissionSlug);
};

export const canEditEmployee = (
  actor: ActorContext,
  target: TargetEmployee,
): 'none' | 'profile-only' | 'full' => {
  if (actor.masqueradingAsSupplier) {
    return 'full';
  }

  if (!canAddTeamMember(actor.permissions)) {
    return 'none';
  }

  if (isSelfEdit(actor, target)) {
    return 'profile-only';
  }

  if (isAdministratorRole(target.role)) {
    if (actor.roleCode != null && !isAdministratorRole(actor.roleCode)) {
      return 'none';
    }
    return 'profile-only';
  }

  if (hasPermission(actor.permissions, PERMISSIONS.editUserPermissions)) {
    return 'full';
  }

  return 'profile-only';
};

export const getProfileEditDisableReason = (
  actor: ActorContext,
  target: TargetEmployee,
): DisableReason | undefined => {
  if (canEditEmployee(actor, target) !== 'none') {
    return undefined;
  }

  if (!canAddTeamMember(actor.permissions)) {
    return DISABLE_REASON.NO_ADD_THERAPISTS;
  }

  if (
    isAdministratorRole(target.role) &&
    actor.roleCode != null &&
    !isAdministratorRole(actor.roleCode)
  ) {
    return DISABLE_REASON.NON_ADMIN_CANNOT_EDIT_ADMIN;
  }

  return DISABLE_REASON.NO_ADD_THERAPISTS;
};

export const canChangeRoleOrPermissions = (
  actor: ActorContext,
  target: TargetEmployee,
): PolicyResult => {
  if (actor.masqueradingAsSupplier) {
    return { allowed: true };
  }

  if (target.supplierBound) {
    return { allowed: false, reason: DISABLE_REASON.SUPPLIER_BOUND };
  }

  if (isSelfEdit(actor, target)) {
    return { allowed: false, reason: DISABLE_REASON.SELF_EDIT_FORBIDDEN };
  }

  if (!canAddTeamMember(actor.permissions)) {
    return { allowed: false, reason: DISABLE_REASON.NO_ADD_THERAPISTS };
  }

  if (!hasPermission(actor.permissions, PERMISSIONS.editUserPermissions)) {
    return { allowed: false, reason: DISABLE_REASON.NO_EDIT_USER_PERMISSIONS };
  }

  const roleForPolicy = getEffectiveTargetRoleForRolePolicy(target);

  if (target.id != null && isAdministratorRole(roleForPolicy)) {
    if (actor.roleCode != null && !isAdministratorRole(actor.roleCode)) {
      return { allowed: false, reason: DISABLE_REASON.NON_ADMIN_CANNOT_EDIT_ADMIN };
    }
    return { allowed: false, reason: DISABLE_REASON.TARGET_ADMIN_IMMUTABLE };
  }

  return { allowed: true };
};

export const hasEmployeeEmail = (emailAddress: string | null | undefined): boolean =>
  !!(emailAddress && String(emailAddress).trim());

export const resolveRolePermissionsDisableState = (
  emailAddress: string | null | undefined,
  roleChangePolicy: PolicyResult,
): { disabled: boolean; reason?: DisableReason } => {
  if (!hasEmployeeEmail(emailAddress)) {
    return { disabled: true, reason: DISABLE_REASON.EMAIL_REQUIRED };
  }

  return {
    disabled: !roleChangePolicy.allowed,
    reason: roleChangePolicy.reason,
  };
};

export const canDeactivateEmployee = (
  actor: ActorContext,
  target: TargetEmployee,
): PolicyResult => {
  const editLevel = canEditEmployee(actor, target);
  if (editLevel === 'none') {
    if (
      isAdministratorRole(target.role) &&
      actor.roleCode != null &&
      !isAdministratorRole(actor.roleCode)
    ) {
      return { allowed: false, reason: DISABLE_REASON.NON_ADMIN_CANNOT_EDIT_ADMIN };
    }
    return { allowed: false, reason: DISABLE_REASON.NO_ADD_THERAPISTS };
  }

  if (isSelfEdit(actor, target)) {
    return { allowed: false, reason: DISABLE_REASON.SELF_DEACTIVATE };
  }

  return { allowed: true };
};

const permissionsForPresetRole = (
  requestedRole: string,
  permissionsByRole: RolePermissions[],
): string[] => {
  const preset = permissionsByRole.find((entry) => entry.role === requestedRole);
  return preset?.permissions ?? [];
};

export const canGrantFullPresetRole = (
  actorPermissions: string[],
  role: string,
  permissionsByRole: RolePermissions[],
): boolean => {
  if (role === ROLES.USER) {
    return true;
  }

  const presetPermissions = permissionsForPresetRole(role, permissionsByRole);
  if (presetPermissions.length === 0) {
    return true;
  }

  return presetPermissions.every((permission) => actorPermissions.includes(permission));
};

const intersectPermissions = (
  requestedPermissions: string[],
  actorPermissions: string[],
): string[] => {
  const actorPermissionSet = new Set(actorPermissions);
  return requestedPermissions.filter((permission) => actorPermissionSet.has(permission));
};

export const mergePermissions = (
  targetCurrent: string[],
  requested: string[],
  actorPermissions: string[],
): string[] => {
  const actorPermissionSet = new Set(actorPermissions);
  const preserved = targetCurrent.filter((permission) => !actorPermissionSet.has(permission));
  const actorControlled = requested.filter((permission) => actorPermissionSet.has(permission));
  return Array.from(new Set([...preserved, ...actorControlled]));
};

export const resolveAllowedRoleAndPermissions = (
  actorPermissions: string[],
  requestedRole: string | null,
  requestedPermissions: string[],
  permissionsByRole: RolePermissions[],
  options: ResolveRolePermissionsOptions = {},
): { role: string | null; permissions: string[] } => {
  const { isCreate = false, currentTarget } = options;

  if (
    !hasPermission(actorPermissions, PERMISSIONS.editUserPermissions) ||
    !hasPermission(actorPermissions, PERMISSIONS.addTherapists)
  ) {
    if (!isCreate && currentTarget) {
      return {
        role: currentTarget.role ?? ROLES.BASIC,
        permissions: currentTarget.permissions ?? [],
      };
    }
    return { role: ROLES.BASIC, permissions: [] };
  }

  if (requestedRole == null) {
    return { role: null, permissions: requestedPermissions };
  }

  const effectiveRequestedPermissions =
    requestedRole === ROLES.USER
      ? requestedPermissions
      : permissionsForPresetRole(requestedRole, permissionsByRole);

  const actorCanGrantAll = effectiveRequestedPermissions.every((permission) =>
    actorPermissions.includes(permission),
  );

  if (actorCanGrantAll) {
    return { role: requestedRole, permissions: effectiveRequestedPermissions };
  }

  if (!isCreate && currentTarget) {
    return {
      role: ROLES.USER,
      permissions: mergePermissions(
        currentTarget.permissions ?? [],
        effectiveRequestedPermissions,
        actorPermissions,
      ),
    };
  }

  return {
    role: ROLES.USER,
    permissions: intersectPermissions(effectiveRequestedPermissions, actorPermissions),
  };
};
